/**
 *
 * @prettier
 * @author Alexander Nagelberg
 */
import { SubmissionError } from 'redux-form'
import { catchCommonErrors } from 'app/actions/commonActionUtils'
import { convertLgyErrorResponseToFormErrorObject } from 'app/utils/formUtils'
import {
  getCookieValue,
  genericPostingActionCreator,
  genericFetchingActionCreator
} from 'app/actions/genericActionCreator'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { fullAPIURL, API_URLs, ENCODINGS, GEAR_URLs } from './lenderConfig'

/**
 * Simple GET. If there's no 'fetched' callback passed, response is dispatched to store.
 *
 * @param {Object} props
 * @param {String} props.URL URL to fetch
 * @param {Function} [props.fetched] Function to callback when fetched. Callback receives
 *  dispatch and responseJson
 */
export const genericGET = ({ URL, fetched }) => async dispatch => {
  dispatch(genericFetchingActionCreator('LENDER_WORKSPACE', 'fetching'))
  dispatch(genericFetchingActionCreator('TRACE', 'fetching'))
  try {
    const response = await fetch(URL)
    const traceId = response?.headers?.get('x-lgyrequestuuid')
    dispatch(genericFetchingActionCreator('TRACE', 'fetched', { responseId: traceId, status: response.status }))
    const responseJson = await catchCommonErrors(dispatch, response, 'fetching lender workspace')
    if (response.status >= 400) {
      dispatch(genericFetchingActionCreator('LENDER_WORKSPACE', 'error', responseJson))
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    } else if (fetched == null) {
      dispatch(genericFetchingActionCreator('LENDER_WORKSPACE', 'fetched', responseJson))
    } else {
      fetched({ dispatch, responseJson })
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }

    console.error(e)
  }
}

/**
 * Simple POST. If there's no 'posted' callback passed, response is dispatched to store.
 *
 * @param {Object} props
 * @param {String} props.URL URL to post
 * @param {Function} [props.posted] Function to callback when posted. Callback receives
 * @param {any} [props.formValues] Function to callback when posted. Callback receives
 *  dispatch and responseJson
 */
export const genericPOST = ({ URL, posted, formValues }) => async dispatch => {
  dispatch(genericPostingActionCreator('LENDER_WORKSPACE', 'posting'))
  dispatch(genericFetchingActionCreator('TRACE', 'fetching'))
  try {
    const response = await fetch(URL, {
      credentials: 'include',
      method: 'POST',
      body: JSON.stringify(formValues),
      headers: new Headers({
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      })
    })
    const traceId = response?.headers?.get('x-lgyrequestuuid')
    dispatch(genericFetchingActionCreator('TRACE', 'fetched', { responseId: traceId, status: response.status }))
    const responseJson = await catchCommonErrors(dispatch, response, 'fetching lender workspace')
    if (response.status >= 400) {
      dispatch(genericPostingActionCreator('LENDER_WORKSPACE', 'error', responseJson))
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    } else if (posted == null) {
      dispatch(genericPostingActionCreator('LENDER_WORKSPACE', 'posted', responseJson))
    } else {
      posted({ dispatch, responseJson })
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }

    console.error(e)
  }
}

/**
 * Generic action creator for posting data using redux form. This will dispatch
 * the response right to the lender store.
 *
 * @param {Object} props
 * @param {String} props.URL URL to fetch
 * @param {String} props.method HTTP method to send (e.g. POST, PATCH, PUT)
 * @param {Function} props.callback Function to callback when posted. Callback receives
 *  dispatch and responseJson
 * @param {Object} props.formValues form values passed in by redux form
 */
export const reduxFormData = ({ URL, method, callback, formValues }) => async dispatch => {
  dispatch(genericPostingActionCreator('LENDER_WORKSPACE', 'posting'))
  dispatch(genericFetchingActionCreator('TRACE', 'fetching'))
  try {
    const response = await fetch(URL, {
      credentials: 'include',
      method,
      body: JSON.stringify(formValues),
      headers: new Headers({
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      })
    })
    const traceId = response?.headers?.get('x-lgyrequestuuid')
    dispatch(genericFetchingActionCreator('TRACE', 'fetched', { responseId: traceId, status: response.status }))
    const responseJson = await catchCommonErrors(dispatch, response, 'updating lender workspace')

    if (response.status >= 400) {
      dispatch(genericPostingActionCreator('LENDER_WORKSPACE', 'error', responseJson))
      console.error(`Error sending form data: ${URL}`)
      throw new SubmissionError(convertLgyErrorResponseToFormErrorObject(responseJson))
    } else if (callback == null) {
      dispatch(genericPostingActionCreator('LENDER_WORKSPACE', 'posted', responseJson))
    } else {
      callback({ dispatch, responseJson })
    }
  } catch (e) {
    if (e instanceof SubmissionError) {
      throw e
    }
    console.error(e)
  }
}

/**
 * Simple function to change pages after receiving server response. Does
 * nothing if no arguments received.
 *
 * @param {Object} [props] if provided, will redirect to page included in server response
 * @param {Object} [props.history] react-router history object to drive location
 * @param {Boolean} [redirect.useWindow] alternative redirect for steering browser instead of react router
 * @param {String} [props.baseUrl] base URL path for gear pages
 * @param {Object} [props.responseJson] server response (contains lastSavedScreen info)
 */
const redirectLocation = ({ history, useWindow, baseUrl, responseJson }) => {
  if (history != null) {
    history.push(`${baseUrl}/${ENCODINGS.lastSavedScreen[responseJson?.lastSavedScreen]}`)
  } else if (useWindow) {
    // kludge mainly used for switching between v1 and v2 spaces
    window.location = `${baseUrl}/${ENCODINGS.lastSavedScreen[responseJson?.lastSavedScreen]}`
  }
}

/**
 * Action for submitting remittance wizard forms. If redirect object is
 * provided, will redirect after receiving response to page designated in
 * response.
 *
 * @param {Object} props required values
 * @param {String} props.URL URL to fetch
 * @param {String} props.method HTTP method to send (e.g. POST, PATCH, PUT)
 * @param {Object} props.formValues form values passed in by redux form
 * @param {Object} [redirect] if provided, will redirect to page included in server response
 * @param {Object} [redirect.history] react-router history object to drive location
 * @param {String} [redirect.baseUrl] base URL path for gear pages
 * @param {Object} [redirect.responseJson] server response (contains lastSavedScreen info)
 */
export const remittanceFormData = ({ URL, method, formValues }, redirect) =>
  reduxFormData({
    URL,
    method,
    formValues,
    callback: ({ dispatch, responseJson }) => {
      dispatch(genericPostingActionCreator('REMITTANCE', 'posted', responseJson))
      redirectLocation({ ...redirect, responseJson })
    }
  })

/**
 * Simple fetch for remittance object on loan. If redirect object is provided,
 * will redirect after receiving response to page designated in response.
 *
 * @param {String} lin URL to fetch including lin
 * @param {Object} [redirect] if provided, will redirect to page included in server response
 * @param {Object} [redirect.history] react-router history object to drive location
 * @param {Boolean} [redirect.useWindow] alternative redirect for steering browser instead of react router
 * @param {String} [redirect.baseUrl] base URL path for gear pages
 * @param {Object} [redirect.responseJson] server response (contains lastSavedScreen info)
 */
export const fetchRemittance = (lin, redirect) =>
  genericGET({
    URL: fullAPIURL(API_URLs.SEARCH_PATH, null, { lin }),
    fetched: ({ dispatch, responseJson }) => {
      dispatch(genericFetchingActionCreator('REMITTANCE', 'fetched', responseJson))
      redirectLocation({ ...redirect, responseJson })
    }
  })

export const deleteRemittance = createAsyncThunk('DELETE_REMITTANCE', async ({ lin, history }, { dispatch }) => {
  const response = await fetch(fullAPIURL(API_URLs.DELETE_REMITTANCE, null, { lin }), {
    credentials: 'include',
    method: 'DELETE',
    headers: new Headers({
      'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
    })
  })
  await catchCommonErrors(dispatch, response, 'deleting remittance ' + lin)
  history.push(GEAR_URLs.GEAR_BASE_PATH)
  return lin
})
